/**
 * Linen Configurator - Multi-Filter Mode
 * Shows filtered product grid based on dropdown selections
 */

(function() {
  'use strict';

  // DOM Elements
  const fabricSelect = document.getElementById('linen-fabric');
  const sizeSelect = document.getElementById('linen-size');
  const colorSelect = document.getElementById('linen-color');
  const productGrid = document.getElementById('linen-product-grid');
  const resultsCount = document.getElementById('linen-results-count');
  const noResults = document.getElementById('linen-no-results');

  // Product data (injected by Liquid)
  let products = [];

  // Initialize
  function init() {
    // Get product data from the page
    const dataElement = document.getElementById('linen-product-data');
    if (dataElement) {
      try {
        products = JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Error parsing linen product data:', e);
        return;
      }
    }

    if (!products.length) {
      console.warn('No linen products found');
      noResults.classList.add('is-visible');
      return;
    }

    // Populate all dropdowns initially
    populateFabricDropdown();
    populateSizeDropdown();
    populateColorDropdown();

    // Event listeners
    fabricSelect.addEventListener('change', onFilterChange);
    sizeSelect.addEventListener('change', onFilterChange);
    colorSelect.addEventListener('change', onFilterChange);

    // Show all products initially
    displayProducts(products);
  }

  // Extract fabric type from product type
  function extractFabricType(productType) {
    if (!productType) return 'Other';
    const match = productType.match(/Linens - Tablecloth\s+(.+)/i);
    return match ? match[1].trim() : 'Other';
  }

  // Populate fabric dropdown
  function populateFabricDropdown() {
    const fabricTypes = new Set();
    products.forEach(function(product) {
      fabricTypes.add(extractFabricType(product.type));
    });

    const sorted = Array.from(fabricTypes).sort();
    fabricSelect.innerHTML = '<option value="">All Fabric Types</option>';
    sorted.forEach(function(fabric) {
      const option = document.createElement('option');
      option.value = fabric;
      option.textContent = fabric;
      fabricSelect.appendChild(option);
    });
    fabricSelect.disabled = false;
  }

  // Check if size looks valid (contains dimensions like "X" or measurements)
  function isValidSize(size) {
    if (!size) return false;
    const sizeStr = String(size).trim();
    // Valid sizes should contain 'x' or 'X' (dimensions) or be longer than 3 chars
    // Filter out single numbers or very short strings
    return sizeStr.length > 3 && (sizeStr.toLowerCase().includes('x') || sizeStr.includes('"') || sizeStr.includes("'"));
  }

  // Populate size dropdown based on current filters
  function populateSizeDropdown(filteredProducts) {
    const currentValue = sizeSelect.value;
    const productsToCheck = filteredProducts || products;

    const sizes = new Set();
    productsToCheck.forEach(function(product) {
      if (product.size && isValidSize(product.size)) sizes.add(product.size);
    });

    // Sort sizes by first number
    const sorted = Array.from(sizes).sort(function(a, b) {
      const strA = String(a || '');
      const strB = String(b || '');
      const matchA = strA.match(/\d+/);
      const matchB = strB.match(/\d+/);
      const numA = matchA ? parseInt(matchA[0]) : 0;
      const numB = matchB ? parseInt(matchB[0]) : 0;
      return numA - numB;
    });

    sizeSelect.innerHTML = '<option value="">All Sizes</option>';
    sorted.forEach(function(size) {
      const option = document.createElement('option');
      option.value = size;
      option.textContent = size;
      sizeSelect.appendChild(option);
    });

    // Restore selection if still valid
    if (currentValue && sorted.includes(currentValue)) {
      sizeSelect.value = currentValue;
    }
    sizeSelect.disabled = false;
  }

  // Populate color dropdown based on current filters
  function populateColorDropdown(filteredProducts) {
    const currentValue = colorSelect.value;
    const productsToCheck = filteredProducts || products;

    const colors = new Set();
    productsToCheck.forEach(function(product) {
      if (product.color) colors.add(product.color);
    });

    const sorted = Array.from(colors).sort();
    colorSelect.innerHTML = '<option value="">All Colors</option>';
    sorted.forEach(function(color) {
      const option = document.createElement('option');
      option.value = color;
      option.textContent = color;
      colorSelect.appendChild(option);
    });

    // Restore selection if still valid
    if (currentValue && sorted.includes(currentValue)) {
      colorSelect.value = currentValue;
    }
    colorSelect.disabled = false;
  }

  // On any filter change
  function onFilterChange() {
    const selectedFabric = fabricSelect.value;
    const selectedSize = sizeSelect.value;
    const selectedColor = colorSelect.value;

    // Filter products
    let filtered = products.filter(function(product) {
      let matches = true;

      if (selectedFabric) {
        matches = matches && extractFabricType(product.type) === selectedFabric;
      }
      if (selectedSize) {
        matches = matches && product.size === selectedSize;
      }
      if (selectedColor) {
        matches = matches && product.color === selectedColor;
      }

      return matches;
    });

    // Update other dropdowns to show only available options
    // But keep fabric dropdown with all options
    let fabricFiltered = products;
    if (selectedFabric) {
      fabricFiltered = products.filter(p => extractFabricType(p.type) === selectedFabric);
    }
    populateSizeDropdown(fabricFiltered);

    let sizeFiltered = fabricFiltered;
    if (selectedSize) {
      sizeFiltered = fabricFiltered.filter(p => p.size === selectedSize);
    }
    populateColorDropdown(sizeFiltered);

    // Display filtered products
    displayProducts(filtered);
  }

  // Display products in grid
  function displayProducts(productsToShow) {
    if (!productsToShow.length) {
      productGrid.innerHTML = '';
      productGrid.classList.remove('is-visible');
      noResults.classList.add('is-visible');
      if (resultsCount) resultsCount.textContent = '0 products';
      return;
    }

    noResults.classList.remove('is-visible');
    if (resultsCount) {
      resultsCount.textContent = productsToShow.length + ' product' + (productsToShow.length === 1 ? '' : 's');
    }

    let html = '';
    productsToShow.forEach(function(product) {
      const stockClass = product.available && product.inventory > 10
        ? 'in-stock'
        : (product.available && product.inventory > 0 ? 'low-stock' : 'out-of-stock');

      const stockText = product.available && product.inventory > 0
        ? 'In stock, ' + product.inventory + ' units'
        : 'Out of stock';

      const fabricType = extractFabricType(product.type);
      html += `
        <div class="linen-grid__item">
          <a href="/products/${product.handle}" class="linen-grid__image-link">
            <div class="linen-grid__image-wrapper">
              <img
                class="linen-grid__image"
                src="${product.image || ''}"
                alt="${product.title}"
                loading="lazy"
                width="300"
                height="300"
              >
            </div>
          </a>
          <div class="linen-grid__info">
            <a href="/products/${product.handle}" class="linen-grid__title-link">
              <h3 class="linen-grid__title">${product.title}</h3>
            </a>
            <div class="linen-grid__meta">
              <span class="linen-grid__fabric" data-filter="fabric" data-value="${fabricType}">${fabricType}</span>
              ${product.size ? `<span class="linen-grid__size" data-filter="size" data-value="${product.size}">${product.size}</span>` : ''}
              ${product.color ? `<span class="linen-grid__color" data-filter="color" data-value="${product.color}">${product.color}</span>` : ''}
            </div>
            <div class="linen-grid__price">${formatMoney(product.price)}</div>
            <div class="linen-grid__stock linen-grid__stock--${stockClass}">
              <span class="linen-grid__stock-dot"></span>
              ${stockText}
            </div>
          </div>
          <div class="linen-grid__cart-row">
            <div class="linen-grid__qty-wrap">
              <button type="button" class="linen-grid__qty-btn" data-action="minus">-</button>
              <input type="number" class="linen-grid__qty-input" value="1" min="1" max="${product.inventory || 99}">
              <button type="button" class="linen-grid__qty-btn" data-action="plus">+</button>
            </div>
            <button
              type="button"
              class="linen-grid__add-btn"
              data-variant-id="${product.variantId}"
              ${!product.available ? 'disabled' : ''}
            >
              ${product.available ? 'Add to Cart' : 'Out of Stock'}
            </button>
          </div>
        </div>
      `;
    });

    productGrid.innerHTML = html;
    productGrid.classList.add('is-visible');

    // Add click handlers for add to cart buttons
    productGrid.querySelectorAll('.linen-grid__add-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const cartRow = btn.closest('.linen-grid__cart-row');
        const qtyInput = cartRow.querySelector('.linen-grid__qty-input');
        const qty = parseInt(qtyInput.value) || 1;
        addToCart(btn, qty);
      });
    });

    // Add click handlers for qty buttons
    productGrid.querySelectorAll('.linen-grid__qty-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const wrap = btn.closest('.linen-grid__qty-wrap');
        const input = wrap.querySelector('.linen-grid__qty-input');
        let val = parseInt(input.value) || 1;
        const max = parseInt(input.max) || 99;

        if (btn.dataset.action === 'minus') {
          val = Math.max(1, val - 1);
        } else {
          val = Math.min(max, val + 1);
        }
        input.value = val;
      });
    });

    // Add click handlers for filter tags
    productGrid.querySelectorAll('[data-filter]').forEach(function(tag) {
      tag.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const filterType = tag.dataset.filter;
        const filterValue = tag.dataset.value;

        if (filterType === 'fabric') {
          fabricSelect.value = filterValue;
        } else if (filterType === 'size') {
          sizeSelect.value = filterValue;
        } else if (filterType === 'color') {
          colorSelect.value = filterValue;
        }

        onFilterChange();

        // Scroll to top of configurator
        document.querySelector('.linen-configurator__selectors-wrapper').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // Format money
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  // Add to cart
  function addToCart(btn, quantity) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    quantity = quantity || 1;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
    .then(response => response.json())
    .then(data => {
      btn.textContent = 'Added!';
      refreshCart();
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }, 2000);
    })
    .catch(error => {
      console.error('Error:', error);
      btn.textContent = 'Error';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }, 2000);
    });
  }

  // Refresh cart - works with Warehouse theme
  function refreshCart() {
    // Update cart count in header
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update all possible cart count selectors
        const countSelectors = [
          '.Header__CartCount',
          '[data-cart-count]',
          '.cart-count',
          '.cart-item-count',
          '.CartCount',
          '.js-cart-count',
          '.header__cart-count'
        ];

        countSelectors.forEach(selector => {
          document.querySelectorAll(selector).forEach(el => {
            el.textContent = cart.item_count;
          });
        });

        // Dispatch events for various themes
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

        // Trigger Shopify's native cart events
        if (window.Shopify && window.Shopify.onCartUpdate) {
          window.Shopify.onCartUpdate(cart);
        }

        // Try theme-specific cart open methods
        if (window.theme && window.theme.openCart) {
          window.theme.openCart();
        }

        // Fetch the cart drawer section and update it
        fetch('/?sections=cart-drawer,mini-cart,cart-sidebar')
          .then(r => r.json())
          .then(sections => {
            // Try each possible section name
            Object.keys(sections).forEach(sectionName => {
              const html = sections[sectionName];
              if (html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // Try to find and replace drawer content
                const possibleSelectors = [
                  '.Drawer__Content',
                  '.cart-drawer__content',
                  '.mini-cart__content',
                  '.cart-sidebar__content',
                  '[data-cart-drawer]',
                  '.ajaxcart__inner'
                ];

                possibleSelectors.forEach(selector => {
                  const newContent = doc.querySelector(selector);
                  const oldContent = document.querySelector(selector);
                  if (newContent && oldContent) {
                    oldContent.innerHTML = newContent.innerHTML;
                  }
                });
              }
            });
          })
          .catch(() => {
            // Fallback: try single section fetch
            fetch('/?section_id=cart-drawer')
              .then(r => r.text())
              .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newDrawer = doc.querySelector('.Drawer__Content, .cart-drawer__content');
                const oldDrawer = document.querySelector('.Drawer__Content, .cart-drawer__content');
                if (newDrawer && oldDrawer) {
                  oldDrawer.innerHTML = newDrawer.innerHTML;
                }
              })
              .catch(() => {});
          });

        // Try to open the cart drawer
        setTimeout(() => {
          // Method 1: Click the cart trigger
          const cartTrigger = document.querySelector('[data-action="open-drawer"][data-drawer-id="cart"], [href="/cart"], .js-cart-trigger, .header__cart-btn');
          if (cartTrigger) {
            cartTrigger.click();
          }

          // Method 2: Add class to drawer
          const drawer = document.querySelector('[data-drawer-id="cart"], .cart-drawer, #cart-drawer');
          if (drawer) {
            drawer.classList.add('is-open', 'is-visible', 'active');
          }
        }, 100);
      })
      .catch(() => {});
  }

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
